import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import type { CandidateWaitingPopup } from "@shared/candidateWaiting";
import {
  browserStorage,
  claimWaitingAlert,
  closeWaitingSystemNotification,
  isDesktopPointer,
  newTabId,
  openWaitingPageNotificationKeys,
  ownsWaitingAlert,
  readDismissedWaiting,
  readNotifyOffered,
  releaseWaitingAlertClaim,
  rememberDismissedWaiting,
  rememberNotifyOffered,
  shouldOfferDesktopNotifications,
  showWaitingSystemNotification,
  WAITING_DISMISSED_KEY,
  WAITING_NOTIFY_OFFERED_KEY,
  waitingAlertDelayMs,
  waitingEnterTarget,
  waitingForThisPage,
  waitingOpenMode,
} from "@/lib/candidateWaitingAlerts";
import { usePageVisible } from "@/hooks/usePageVisible";

/**
 * Пауза перед звонком: две вкладки могли застолбить одно ожидание почти
 * одновременно, звонит та, чья запись в хранилище осталась последней.
 */
const CLAIM_SETTLE_MS = 300;

/** Человек хоть раз нажимал что-то в этой вкладке — значит, звук ей разрешён. */
function tabHasBeenActive(): boolean {
  // Старый Safari не знает userActivation — считаем вкладку обычной.
  return navigator.userActivation?.hasBeenActive ?? true;
}

function notificationPermission(): NotificationPermission | null {
  return typeof Notification === "undefined" ? null : Notification.permission;
}

/**
 * Поведение карточек «кандидат ждёт вас в комнате» во вкладке: что показать,
 * когда позвонить, куда вести кнопку «Войти в комнату».
 *
 *  • Закрыл в одной вкладке — закрыто во всех (localStorage + событие storage).
 *  • Звук и системное уведомление — один раз на период ожидания, одной
 *    вкладкой из всех открытых. Вкладка на экране звонит сразу, скрытая —
 *    только если за 20 секунд этого не сделала видимая. Карточка без звука
 *    (рекрутёр на другой встрече) не звонит; перестала быть «тихой» —
 *    позвонит тогда.
 *  • Системное уведомление — только из скрытой вкладки и если разрешение уже
 *    дано; снимается, как только карточка исчезла, и сразу по «Войти» или
 *    «Скрыть».
 *  • Из живой встречи «Войти в комнату» открывает новую вкладку.
 *  • Экран проверки камеры тоже получает карточку и звук. Её кнопка
 *    использует вход открытой комнаты и не создаёт вторую вкладку.
 */
export function useCandidateWaitingAlerts(
  waiting: readonly CandidateWaitingPopup[],
  chime: (isCurrent?: () => boolean) => boolean | Promise<boolean>,
  onSounded?: (item: CandidateWaitingPopup) => void
) {
  const [location, navigate] = useLocation();
  const storage = useMemo(() => browserStorage(), []);
  const tabId = useMemo(() => newTabId(), []);
  const [dismissed, setDismissed] = useState<Set<string>>(() =>
    storage ? readDismissedWaiting(storage) : new Set()
  );
  const [permission, setPermission] = useState(notificationPermission);
  const [offered, setOffered] = useState(() => readNotifyOffered(storage));
  const pageVisible = usePageVisible();
  const [interaction, setInteraction] = useState(0);
  const aliveRef = useRef(true);
  const soundingRef = useRef<Set<string>>(new Set());
  /** Периоды, по которым эта вкладка уже решила, звонить ли. */
  const handledRef = useRef<Set<string>>(new Set());
  /** Периоды, которые эта вкладка видела на экране: их уведомления снимаем, когда уйдут. */
  const shownRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<number[]>([]);
  /** Отложенные звонки скрытой вкладки: период → таймер. */
  const pendingRef = useRef<Map<string, number>>(new Map());
  /** Последний список карточек — для отложенного звонка, который срабатывает позже рендера. */
  const latestRef = useRef<readonly CandidateWaitingPopup[]>([]);
  // A fresh tab may display a waiting notice before Chrome allows sound.
  // Retry on the first permitted interaction; do not consume its sound early.
  useEffect(() => {
    aliveRef.current = true;
    const interacted = () => setInteraction(value => value + 1);
    window.addEventListener("pointerdown", interacted);
    window.addEventListener("keydown", interacted);
    return () => {
      aliveRef.current = false;
      window.removeEventListener("pointerdown", interacted);
      window.removeEventListener("keydown", interacted);
      if (storage)
        for (const key of soundingRef.current)
          releaseWaitingAlertClaim(storage, key, tabId);
    };
  }, [storage, tabId]);

  // Соседняя вкладка закрыла карточку или уже спросила про уведомления.
  useEffect(() => {
    if (!storage) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key === WAITING_DISMISSED_KEY)
        setDismissed(readDismissedWaiting(storage));
      if (event.key === WAITING_NOTIFY_OFFERED_KEY)
        setOffered(readNotifyOffered(storage));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storage]);

  // Разрешение могли дать или отозвать в настройках браузера, пока вкладка жила.
  useEffect(() => {
    setPermission(notificationPermission());
  }, [pageVisible]);

  useEffect(
    () => () => {
      timersRef.current.forEach(timer => window.clearTimeout(timer));
      pendingRef.current.forEach(timer => window.clearTimeout(timer));
    },
    []
  );

  const visible = useMemo(
    () =>
      waitingForThisPage(waiting, location).filter(
        item => !dismissed.has(item.key)
      ),
    [waiting, dismissed, location]
  );

  const dismiss = useCallback(
    (item: CandidateWaitingPopup) => {
      setDismissed(
        storage
          ? rememberDismissedWaiting(storage, item.key)
          : current => new Set(current).add(item.key)
      );
      // Сразу, а не проходом ниже: переход в комнату пересоздаёт карточки
      // раньше, чем этот проход успеет увидеть, что карточки больше нет.
      void closeWaitingSystemNotification(item.key);
    },
    [storage]
  );

  const enter = useCallback(
    (item: CandidateWaitingPopup) => {
      const target = waitingEnterTarget(location, item.link);
      if (target === "here") {
        // Keep the notice until joining succeeds or the user explicitly closes
        // it. A disabled join button (e.g. no microphone) must not dismiss it.
        const join =
          document.querySelector<HTMLButtonElement>(".video-room-join");
        join?.focus();
        if (join && !join.disabled) join.click();
        return;
      }
      // Рекрутёр пошёл в комнату — карточка больше не нужна ни в одной вкладке,
      // не дожидаясь, пока сервер увидит его там на следующем опросе.
      dismiss(item);
      if (target === "new-tab") {
        window.open(item.link, "_blank", "noopener");
      } else {
        navigate(item.link);
      }
    },
    [dismiss, navigate, location]
  );

  /** Застолбить звонок за этой вкладкой и, если достался ей, позвонить. */
  const alertOnce = useCallback(
    (item: CandidateWaitingPopup) => {
      if (soundingRef.current.has(item.key) || !tabHasBeenActive()) return;
      if (storage && !claimWaitingAlert(storage, item.key, tabId)) return;
      soundingRef.current.add(item.key);
      const timer = window.setTimeout(() => {
        timersRef.current = timersRef.current.filter(id => id !== timer);
        if (storage && !ownsWaitingAlert(storage, item.key, tabId)) {
          soundingRef.current.delete(item.key);
          return;
        }
        const isCurrent = () =>
          aliveRef.current &&
          latestRef.current.some(
            current => current.key === item.key && !current.silent
          );
        void Promise.resolve(isCurrent() ? chime(isCurrent) : false)
          .then(played => {
            if (played) {
              handledRef.current.add(item.key);
              onSounded?.(item);
            } else if (storage)
              releaseWaitingAlertClaim(storage, item.key, tabId);
            if (isCurrent() && document.visibilityState === "hidden") {
              void showWaitingSystemNotification(item, () => enter(item));
            }
          })
          .catch(() => {
            if (storage) releaseWaitingAlertClaim(storage, item.key, tabId);
          })
          .finally(() => soundingRef.current.delete(item.key));
      }, CLAIM_SETTLE_MS);
      timersRef.current.push(timer);
    },
    [storage, tabId, chime, enter, onSounded]
  );

  // Системные уведомления, показанные прежним экземпляром, — тоже наши. При
  // переходе в живую встречу и обратно DashboardLayout меняет обёртку, карточки
  // пересоздаются, и память о показанном пропадает; без этого уведомление,
  // которое «висит, пока не нажмут», осталось бы висеть. Проход ниже закроет
  // те, чьих карточек здесь нет. Он объявлен позже и потому идёт после.
  useEffect(() => {
    for (const key of openWaitingPageNotificationKeys())
      shownRef.current.add(key);
  }, []);

  // Звук и системное уведомление — один раз на период, одной вкладкой.
  useEffect(() => {
    latestRef.current = visible;
    const current = new Set(visible.map(item => item.key));
    for (const item of visible) {
      shownRef.current.add(item.key);
      if (item.silent || handledRef.current.has(item.key)) continue;
      const delay = waitingAlertDelayMs({
        pageVisible,
        hasBeenActive: tabHasBeenActive(),
      });
      if (delay === 0) {
        const pending = pendingRef.current.get(item.key);
        if (pending !== undefined) window.clearTimeout(pending);
        pendingRef.current.delete(item.key);
        alertOnce(item);
        continue;
      }
      if (pendingRef.current.has(item.key)) continue;
      const timer = window.setTimeout(() => {
        pendingRef.current.delete(item.key);
        // Вкладку за это время открыли — решит ветка «на экране».
        if (document.visibilityState !== "hidden") return;
        // Карточку закрыли, рекрутёр вошёл или сейчас на другой встрече.
        const latest = latestRef.current.find(entry => entry.key === item.key);
        if (!latest || latest.silent || handledRef.current.has(item.key))
          return;
        alertOnce(latest);
      }, delay);
      pendingRef.current.set(item.key, timer);
    }
    for (const [key, timer] of [...pendingRef.current]) {
      if (current.has(key)) continue;
      window.clearTimeout(timer);
      pendingRef.current.delete(key);
    }
    // Карточка ушла (рекрутёр вошёл, кандидат ушёл, закрыли) — уведомление тоже.
    for (const key of [...shownRef.current]) {
      if (current.has(key)) continue;
      shownRef.current.delete(key);
      void closeWaitingSystemNotification(key);
    }
  }, [visible, pageVisible, interaction, alertOnce]);

  const offerNotifications = shouldOfferDesktopNotifications({
    supported: permission !== null,
    permission,
    alreadyOffered: offered,
    desktop: isDesktopPointer(),
  });

  const enableNotifications = useCallback(() => {
    rememberNotifyOffered(storage);
    setOffered(true);
    if (typeof Notification === "undefined") return;
    // Спрашиваем только по нажатию: браузеры глушат сайты, которые просят сами.
    void Notification.requestPermission()
      .then(result => setPermission(result))
      .catch((error: unknown) => {
        console.warn(
          "[CandidateWaiting] разрешение на уведомления не запрошено:",
          error
        );
        setPermission(notificationPermission());
      });
  }, [storage]);

  return {
    visible,
    enter,
    dismiss,
    /** Сейчас открыта живая встреча: комната откроется в новой вкладке. */
    opensNewTab: waitingOpenMode(location) === "new-tab",
    offerNotifications,
    enableNotifications,
  };
}
